import { apiClient } from '@/api/client'
import type {
  CandidatePatchParams,
  ConfirmSpotParams,
  CreateImportJobParams,
  CreateItemParams,
  CreateRallyParams,
  CreateStampParams,
  ImportJob,
  ImportJobSummary,
  ItemPlaceResult,
  LinkedPlace,
  PlaceSearchResult,
  RallyMapItem,
  RallyMapItemsParams,
  RallyStatus,
  StampRallyDetail,
  StampRallyItem,
  StampRallySummary,
  UpdateItemParams,
  UpdateRallyParams,
  VisitSuggestion,
} from '@/api/types/stamp-rally'

export const getStampRallies = async (
  params?: { status?: RallyStatus },
  signal?: AbortSignal,
): Promise<StampRallySummary[]> => {
  const res = await apiClient.get('/stamp-rallies', { params, signal })
  return res.data
}

export const getStampRally = async (rallyId: number, signal?: AbortSignal): Promise<StampRallyDetail> => {
  const res = await apiClient.get(`/stamp-rallies/${rallyId}`, { signal })
  return res.data
}

/** ラリーをまたいだスポット。座標を持つものだけが返る */
export const getRallyMapItems = async (params?: RallyMapItemsParams, signal?: AbortSignal): Promise<RallyMapItem[]> => {
  // axios の既定は `rally_ids[]=1` 形式で FastAPI が list として受け取れないため、自前で組む
  const search = new URLSearchParams()
  for (const id of params?.rally_ids ?? []) search.append('rally_ids', String(id))
  for (const id of params?.item_ids ?? []) search.append('item_ids', String(id))
  if (params?.only_unstamped) search.append('only_unstamped', 'true')

  const res = await apiClient.get(`/stamp-rallies/map-items?${search.toString()}`, { signal })
  return res.data
}

export const createStampRally = async (params: CreateRallyParams): Promise<StampRallySummary> => {
  const res = await apiClient.post('/stamp-rallies', params)
  return res.data
}

export const updateStampRally = async (rallyId: number, params: UpdateRallyParams): Promise<StampRallySummary> => {
  const res = await apiClient.patch(`/stamp-rallies/${rallyId}`, params)
  return res.data
}

export const deleteStampRally = async (rallyId: number): Promise<void> => {
  await apiClient.delete(`/stamp-rallies/${rallyId}`)
}

export const createStampRallyItem = async (rallyId: number, params: CreateItemParams): Promise<StampRallyItem> => {
  const res = await apiClient.post(`/stamp-rallies/${rallyId}/items`, params)
  return res.data
}

export const updateStampRallyItem = async (itemId: number, params: UpdateItemParams): Promise<StampRallyItem> => {
  const res = await apiClient.patch(`/stamp-rallies/items/${itemId}`, params)
  return res.data
}

export const deleteStampRallyItem = async (itemId: number): Promise<void> => {
  await apiClient.delete(`/stamp-rallies/items/${itemId}`)
}

// --- スポットと場所の連携 -------------------------------------------------

export const getItemPlace = async (itemId: number, signal?: AbortSignal): Promise<LinkedPlace | null> => {
  const res = await apiClient.get(`/stamp-rallies/items/${itemId}/place`, { signal })
  return res.data.place
}

/** /place-search の結果をそのまま渡して場所を差し替える */
export const linkItemPlace = async (itemId: number, result: PlaceSearchResult): Promise<ItemPlaceResult> => {
  const res = await apiClient.put(`/stamp-rallies/items/${itemId}/place`, {
    google_place_id: result.google_place_id,
    name: result.name,
    address: result.address,
    latitude: result.latitude,
    longitude: result.longitude,
    prefecture: result.prefecture,
    city: result.city,
  })
  return res.data
}

export const createStamp = async (itemId: number, params: CreateStampParams = {}): Promise<StampRallyItem> => {
  const res = await apiClient.post(`/stamp-rallies/items/${itemId}/stamp`, params)
  return res.data
}

export const deleteStamp = async (itemId: number): Promise<StampRallyItem> => {
  const res = await apiClient.delete(`/stamp-rallies/items/${itemId}/stamp`)
  return res.data
}

/**
 * スポット追加用の場所検索。Google Places の無料枠を消費するので入力確定時にだけ呼ぶこと。
 * origin を渡すと近場を優先し、結果に距離が付く。
 */
export const searchRallyPlaces = async (
  query: string,
  origin?: { latitude: number; longitude: number } | null,
  signal?: AbortSignal,
): Promise<PlaceSearchResult[]> => {
  const params: Record<string, string | number> = { query }
  if (origin) {
    params.latitude = origin.latitude
    params.longitude = origin.longitude
  }
  const res = await apiClient.get('/stamp-rallies/place-search', { params, signal })
  return res.data
}

// --- AI 一括登録 -------------------------------------------------------------

export const createImportJob = async (params: CreateImportJobParams): Promise<ImportJob> => {
  const res = await apiClient.post('/stamp-rally-imports', params)
  return res.data
}

export const getImportJob = async (jobId: number, signal?: AbortSignal): Promise<ImportJob> => {
  const res = await apiClient.get(`/stamp-rally-imports/${jobId}`, { signal })
  return res.data
}

/** 進行中のジョブだけが返る。画面を離れた取り込みを拾い直すのに使う */
export const listImportJobs = async (rallyId?: number, signal?: AbortSignal): Promise<ImportJobSummary[]> => {
  const res = await apiClient.get('/stamp-rally-imports', {
    params: rallyId === undefined ? undefined : { rally_id: rallyId },
    signal,
  })
  return res.data
}

/** 確認①の確定。承認した件数だけ Google Places の枠を消費する */
export const confirmImportSpots = async (
  jobId: number,
  spots: ConfirmSpotParams[],
  searchPrefix?: string | null,
  searchSuffix?: string | null,
): Promise<ImportJob> => {
  const res = await apiClient.post(`/stamp-rally-imports/${jobId}/confirm-spots`, {
    spots,
    search_prefix: searchPrefix ?? null,
    search_suffix: searchSuffix ?? null,
  })
  return res.data
}

/** 確認②。曖昧だった候補を手で確定・除外する */
export const patchImportCandidates = async (jobId: number, candidates: CandidatePatchParams[]): Promise<ImportJob> => {
  const res = await apiClient.patch(`/stamp-rally-imports/${jobId}`, { candidates })
  return res.data
}

export const applyImportJob = async (jobId: number): Promise<{ created: number; skipped: number; job: ImportJob }> => {
  const res = await apiClient.post(`/stamp-rally-imports/${jobId}/apply`)
  return res.data
}

export const deleteImportJob = async (jobId: number): Promise<void> => {
  await apiClient.delete(`/stamp-rally-imports/${jobId}`)
}

// --- 訪問履歴からの提案 -----------------------------------------------------

/** 未獲得スポットのうち、過去に訪れている記録があるものを返す。提案だけでスタンプは押されない */
export const getVisitSuggestions = async (rallyId: number, signal?: AbortSignal): Promise<VisitSuggestion[]> => {
  const res = await apiClient.get(`/stamp-rallies/${rallyId}/visit-suggestions`, { signal })
  return res.data
}

/** 提案の一括承認。スタンプ日時はサーバ側が訪問日から決める */
export const createStampsBulk = async (
  rallyId: number,
  itemIds: number[],
): Promise<{ created: number; skipped: number }> => {
  const res = await apiClient.post(`/stamp-rallies/${rallyId}/stamps/bulk`, { item_ids: itemIds })
  return res.data
}

/** 行ったがスタンプは取れなかった訪問を却下する。他の訪問が残っていれば次の候補が繰り上がる */
export const dismissVisitSuggestion = async (rallyId: number, itemId: number, visitId: number): Promise<void> => {
  await apiClient.post(`/stamp-rallies/${rallyId}/visit-suggestions/dismiss`, {
    item_id: itemId,
    visit_id: visitId,
  })
}

/** 却下の取り消し */
export const restoreVisitSuggestion = async (rallyId: number, itemId: number, visitId: number): Promise<void> => {
  await apiClient.delete(`/stamp-rallies/${rallyId}/visit-suggestions/dismiss`, {
    params: { item_id: itemId, visit_id: visitId },
  })
}
